// Importing necessary dependencies
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";
import { messages } from "../../utils/messages";

// Initial state for the user slice
const initialState = {
  userData: null,
  status: "idle",
  changePasswordSuccess: false,
};

// Destructuring Http function from the Http service
const { httpRequest } = Http();

// Async thunk to change password
// Keep the existing thunk but ensure it matches the data structure
export const changePassword = createAsyncThunk(
  "changePassword",
  async (
    {
      old_password,
      new_password,
      new_password_confirmation,
      navigate,
      setLoader,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await httpRequest(
        {
          method: "PUT",
          url: "admin/profile/password",
          body: {
            old_password,
            new_password,
            new_password_confirmation,
          },
        },
        navigate
      );

      // response.status === 204 usually has no content, but it still means success
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "An error occurred");
    }
  }
);

// Async thunk to fetch user profile
export const fetchUserProfile = createAsyncThunk(
  "fetchUserProfile",
  async ({ navigate, setLoader }, { rejectWithValue }) => {
    try {
      const response = await httpRequest(
        {
          method: "GET",
          url: "me",
        },
        navigate
      );
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "An error occurred", {
        cause: err.cause,
      });
    }
  }
);

// Slice for managing user state
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    // Reducer to show toast message
    showToast: (state, action) => {
      const { success, message } = action.payload;
      toastHandler(success, message);
    },
    // Reducer to set user data
    setUserData: (state, action) => {
      console.log("state", state);
      console.log("action", action.payload);

      state.userData = action.payload.user;
    },
    // Reducer to reset password state
    resetPasswordState: (state) => {
      state.changePasswordSuccess = false;
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Reducer to handle pending actions
      .addMatcher(
        (action) =>
          [changePassword.pending, fetchUserProfile.pending].some((type) =>
            type.match(action)
          ),
        (state, action) => {
          state.status = "loading";
          if (action.meta.arg?.setLoader) {
            action.meta.arg.setLoader(true);
          }
        }
      )
      // Reducer to handle fulfilled actions
      .addMatcher(
        (action) =>
          [changePassword.fulfilled, fetchUserProfile.fulfilled].some((type) =>
            type.match(action)
          ),
        (state, action) => {
          state.status = "succeeded";
          if (action.meta.arg?.setLoader) {
            action.meta.arg.setLoader(false);
          }

          if (action.type.includes("changePassword")) {
            state.changePasswordSuccess = true;
            // Show success toast
            userSlice.caseReducers.showToast(state, {
              payload: {
                success: true,
                message:
                  action?.payload?.message || messages.changePassword.success,
              },
            });
          }

          if (action.type.includes("fetchUserProfile")) {
            state.userData = action.payload.user;
          }
        }
      )
      // Reducer to handle rejected actions
      .addMatcher(
        (action) =>
          [changePassword.rejected, fetchUserProfile.rejected].some((type) =>
            type.match(action)
          ),
        (state, action) => {
          state.status = "failed";
          state.changePasswordSuccess = false;

          if (action.meta.arg?.setLoader) {
            action.meta.arg.setLoader(false);
          }

          // Show error toast
          userSlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message:
                action.payload || messages[action.type.split("/")[0]].error,
            },
          });
        }
      );
  },
});

// Action creators are generated for each case reducer function
export const { setUserData, resetPasswordState } = userSlice.actions;

// Exporting reducer
export default userSlice.reducer;